import React from 'react';
import { db, auth } from './firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  doc, 
  getDoc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { Domain, ClickEvent } from '../types';

const DOMAINS_COL = 'domains';
const EVENTS_COL = 'events';

export const getUserRole = async (uid: string) => {
  try {
    const userRef = doc(db, 'users', uid);
    const snap = await getDoc(userRef);
    return snap.exists() ? snap.data().role : 'user';
  } catch (e) {
    return 'user';
  }
};

export const getDomains = async (): Promise<Domain[]> => {
  const user = auth.currentUser;
  if (!user) return [];
  
  const role = await getUserRole(user.uid);
  
  let q;
  if (role === 'admin') {
    q = query(collection(db, DOMAINS_COL), orderBy("createdAt", "desc"));
  } else {
    q = query(collection(db, DOMAINS_COL), where("userId", "==", user.uid));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Domain[];
};

export const getAllUsers = async () => {
  const user = auth.currentUser;
  if (!user) return [];
  const role = await getUserRole(user.uid);
  if (role !== 'admin') return [];

  const querySnapshot = await getDocs(collection(db, 'users'));
  return querySnapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data()
  }));
};

export const updateUserRole = async (targetUid: string, newRole: 'admin' | 'user') => {
  const userRef = doc(db, 'users', targetUid);
  await updateDoc(userRef, { role: newRole });
};

export const updateUserStatus = async (targetUid: string, isActive: boolean) => {
  const userRef = doc(db, 'users', targetUid);
  await updateDoc(userRef, { isActive });
};

export const saveDomain = async (domain: Omit<Domain, 'id'>): Promise<Domain | null> => {
  const user = auth.currentUser;
  if (!user) throw new Error("Must be logged in to save domain");
  
  const payload = {
    ...domain,
    userId: user.uid,
    userEmail: user.email 
  };
  
  const docRef = await addDoc(collection(db, DOMAINS_COL), payload);
  return { id: docRef.id, ...payload } as Domain;
};

export const getDomainById = async (id: string): Promise<Domain | null> => {
  const docRef = doc(db, DOMAINS_COL, id);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as Domain;
  } else {
    return null;
  }
};

export const deleteDomain = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, DOMAINS_COL, id));
};

export const updateDomain = async (id: string, updates: Partial<Domain>): Promise<void> => {
  const docRef = doc(db, DOMAINS_COL, id);
  await updateDoc(docRef, updates);
};

export const getEvents = async (domainId?: string): Promise<ClickEvent[]> => {
  let q;
  if (domainId) {
    q = query(
      collection(db, EVENTS_COL), 
      where("domainId", "==", domainId),
      orderBy("timestamp", "desc"),
      limit(10000) // Increased limit to 10k for better data history
    );
  } else {
    q = query(
      collection(db, EVENTS_COL), 
      orderBy("timestamp", "desc"),
      limit(10000)
    );
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ClickEvent[];
};

export const trackClick = async (domainId: string, e: React.MouseEvent, source: string | null): Promise<void> => {
  const data = {
    domainId,
    source: source || 'direct',
    path: window.location.pathname,
    x: (e.clientX / window.innerWidth) * 100,
    y: (e.clientY / window.innerHeight) * 100,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    timestamp: Date.now(),
    referrer: document.referrer,
    language: navigator.language
  };

  try {
    const response = await fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      console.warn('Tracking failed:', response.status);
    }
  } catch (err) {
    console.debug('Tracking network error suppressed');
  }
};